export const DATA_COMPONENT_ATTRIBUTE = 'data-component';

/**
 * State and internal marker attributes Base UI sets on the elements it renders
 * (collected from `@base-ui/react`'s `*DataAttributes` exports). Base UI merges
 * element props over its state attributes, so a consumer value would override
 * them — and persist while the state is off, since Base UI omits the attribute
 * then. `reservedDataAttributes.test.ts` fails if this list drifts from the
 * installed Base UI version.
 */
export const BASE_UI_DATA_ATTRIBUTES = [
  'data-activation-direction',
  'data-active',
  'data-align',
  'data-anchor-hidden',
  'data-base-ui-click-trigger',
  'data-base-ui-focus-guard',
  'data-base-ui-focusable',
  'data-base-ui-inert',
  'data-base-ui-navigation-menu-trigger',
  'data-base-ui-portal',
  'data-base-ui-slider-control',
  'data-base-ui-slider-indicator',
  'data-base-ui-swipe-ignore',
  'data-base-ui-tooltip-trigger',
  'data-behind',
  'data-checked',
  'data-closed',
  'data-complete',
  'data-current',
  'data-direction',
  'data-dirty',
  'data-disabled',
  'data-dragging',
  'data-empty',
  'data-ending-style',
  'data-error',
  'data-expanded',
  'data-filled',
  'data-focusable',
  'data-focused',
  'data-has-overflow-x',
  'data-has-overflow-y',
  'data-has-submenu-open',
  'data-hidden',
  'data-highlighted',
  'data-hovering',
  'data-indeterminate',
  'data-index',
  'data-instant',
  'data-invalid',
  'data-limited',
  'data-list-empty',
  'data-loading',
  'data-modal',
  'data-multiple',
  'data-nested',
  'data-nested-dialog-open',
  'data-nested-drawer-open',
  'data-nested-drawer-swiping',
  'data-open',
  'data-orientation',
  'data-overflow-x-end',
  'data-overflow-x-start',
  'data-overflow-y-end',
  'data-overflow-y-start',
  'data-panel-open',
  'data-placeholder',
  'data-popup-open',
  'data-popup-side',
  'data-pressed',
  'data-previous',
  'data-progressing',
  'data-readonly',
  'data-required',
  'data-scrolling',
  'data-scrubbing',
  'data-selected',
  'data-side',
  'data-starting-style',
  'data-swipe-direction',
  'data-swipe-dismiss',
  'data-swiping',
  'data-touched',
  'data-transitioning',
  'data-trigger-disabled',
  'data-type',
  'data-uncentered',
  'data-unchecked',
  'data-valid',
  'data-visible',
] as const;

/**
 * Attributes Trellis sets or styles against (`[data-*]` selectors), including
 * the `data-test-id` owned by `dataTestId` and the `data-component` identity
 * marker. Add to this list when a component starts setting or styling a new one.
 */
export const TRELLIS_DATA_ATTRIBUTES = [
  'data-chip-dismiss',
  'data-clickable',
  'data-code-scroll',
  'data-collapser-item',
  'data-collapsible',
  'data-combobox-focus-guard',
  'data-component',
  'data-dismiss-button',
  'data-focus-visible',
  'data-hovered',
  'data-inset',
  'data-inset-left',
  'data-inset-right',
  'data-layout',
  'data-pictogram-name',
  'data-pseudo-state',
  'data-range-end',
  'data-range-middle',
  'data-range-start',
  'data-read-only',
  'data-removable',
  'data-selected-single',
  'data-separator',
  'data-size',
  'data-slot',
  'data-state',
  'data-test-id',
  'data-toast-actions',
  'data-trellis-tag',
  'data-validation-status',
  'data-variant',
] as const;

/**
 * `data-*` attributes consumers cannot set through a Config API component's
 * `dataAttributes` prop.
 */
export const RESERVED_DATA_ATTRIBUTES = [
  ...BASE_UI_DATA_ATTRIBUTES,
  ...TRELLIS_DATA_ATTRIBUTES,
] as const;

/**
 * Merges a component's own name with any `data-component` value it inherited from a
 * composed parent, producing a deduped, space-separated token list (e.g.
 * `"DatePicker InputGroup"`). When one component is rendered as another's root — via a
 * Base UI `render` prop or direct composition — this preserves both identities instead
 * of the inner name clobbering the outer one. With no inherited value it returns just
 * `name`, a single token identical to a standalone component. Consumers should match it
 * with the `~=` attribute selector rather than exact `=`.
 */
export function mergeDataComponent(
  inherited: string | undefined,
  name: string
): string {
  if (!inherited) return name;
  const tokens = [...new Set(inherited.split(' ').filter(Boolean))];
  if (!tokens.includes(name)) tokens.push(name);
  return tokens.join(' ');
}
